import { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import CompetencyBar from './CompetencyBar';
import type { CompetencyResult } from '../../types/api';

interface PrimaryCompetenciesListProps {
  competencies: (CompetencyResult | string)[];
  onKeywordClick: (keyword: string, e: React.MouseEvent) => void;
  emptyLabel: string;
}

export default function PrimaryCompetenciesList({
  competencies,
  onKeywordClick,
  emptyLabel,
}: PrimaryCompetenciesListProps) {
  const primaries = useMemo(
    () =>
      competencies
        .filter((c): c is CompetencyResult => typeof c === 'object' && !!c.is_primary)
        .sort((a, b) => {
          const aImp = typeof a.importance === 'number' ? a.importance : 0;
          const bImp = typeof b.importance === 'number' ? b.importance : 0;
          if (aImp !== bImp) return bImp - aImp;
          return (a.keyword || '').localeCompare(b.keyword || '');
        }),
    [competencies],
  );

  if (primaries.length === 0) {
    return <p className="text-sm text-ctu-text-muted">{emptyLabel}</p>;
  }

  return (
    <ol className="m-0 flex flex-col divide-y divide-border list-none p-0">
      {primaries.map((comp, idx) => {
        const keyword = comp.keyword || '';
        const importance = typeof comp.importance === 'number' ? comp.importance : 0;
        return (
          <li key={`${keyword}-${idx}`} className="group">
            <a
              href={`/competencies/${encodeURIComponent(keyword)}`}
              onClick={e => onKeywordClick(keyword, e)}
              className="flex items-center gap-4 py-2.5 no-underline transition-colors hover:bg-muted/40 -mx-2 px-2 rounded"
            >
              <span className="w-5 shrink-0 text-right text-xs font-mono tabular-nums text-ctu-text-muted">
                {idx + 1}
              </span>
              <span className="flex-1 min-w-0 truncate text-sm font-medium text-ctu-text">
                {keyword}
              </span>
              <CompetencyBar importance={importance} className="hidden sm:block w-24 shrink-0" />
              <span className="w-10 shrink-0 text-right text-xs font-mono tabular-nums text-ctu-text-muted">
                {importance.toFixed(2)}
              </span>
              <ChevronRight
                aria-hidden
                className="h-3.5 w-3.5 shrink-0 text-ctu-text-muted opacity-0 transition-opacity group-hover:opacity-100"
              />
            </a>
          </li>
        );
      })}
    </ol>
  );
}
